import { useCallback, useEffect, useRef, useState } from 'react';

import { type BoardView, fetchBoard } from '../api.ts';
import { DASHBOARD_POLL_MS, describeError, redirectIfUnauthorised } from '../data.tsx';

/** What the read asks the server to leave in (US-016): `?archived=1`, `?posted=all`. */
export interface BoardReadOptions {
  readonly archived?: boolean;
  readonly postedAll?: boolean;
}

export interface BoardRead {
  /** `null` until the first read lands. */
  readonly board: BoardView | null;
  /** The last failure to read the board; the previous board stays on screen. */
  readonly error: string | null;
  /** Re-reads the board now, after an action changed something. */
  readonly refresh: () => Promise<void>;
}

/**
 * The board, kept live: read on the dashboard's beat while the tab is shown,
 * read again as soon as it is shown after being hidden, and never after the
 * page has gone.
 */
export function useBoard(options: BoardReadOptions = {}): BoardRead {
  const archived = options.archived === true;
  const postedAll = options.postedAll === true;
  const [board, setBoard] = useState<BoardView | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** The page's controller; aborting it ends every read, the one in flight too. */
  const controller = useRef<AbortController | null>(null);
  /**
   * The signal of the read in flight, so a poll never stacks behind a slow
   * one. A signal rather than a boolean for the reason `AppDataProvider`
   * gives: StrictMode aborts the first mount's read before it has settled.
   */
  const inFlight = useRef<AbortSignal | null>(null);
  /** A `refresh()` that arrived mid-read: the read after it must still happen. */
  const again = useRef(false);
  /** The options of the current mount of the effect, read by every load. */
  const asked = useRef({ archived, postedAll });

  const load = useCallback(async (signal: AbortSignal, queue = false): Promise<void> => {
    if (inFlight.current !== null && !inFlight.current.aborted) {
      if (queue) again.current = true;
      return;
    }
    inFlight.current = signal;
    try {
      setBoard(await fetchBoard({ ...asked.current, signal }));
      setError(null);
    } catch (cause: unknown) {
      if (signal.aborted) return;
      if (redirectIfUnauthorised(cause)) return;
      setError(describeError(cause));
    } finally {
      if (inFlight.current === signal) inFlight.current = null;
    }
    if (again.current && !signal.aborted) {
      again.current = false;
      await load(signal);
    }
  }, []);

  useEffect(() => {
    // Changing the options aborts the read for the old ones and starts afresh.
    asked.current = { archived, postedAll };
    const current = new AbortController();
    controller.current = current;
    const visible = (): boolean => document.visibilityState === 'visible';

    void load(current.signal);
    const timer = window.setInterval(() => {
      if (visible()) void load(current.signal);
    }, DASHBOARD_POLL_MS);
    const onVisible = (): void => {
      if (visible()) void load(current.signal);
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      current.abort();
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load, archived, postedAll]);

  const refresh = useCallback(async (): Promise<void> => {
    const current = controller.current;
    if (current === null || current.signal.aborted) return;
    await load(current.signal, true);
  }, [load]);

  return { board, error, refresh };
}
